/**
 * WorkspaceConfigLoader - Loads and instantiates DDD structure from JSON config.
 *
 * This loader handles:
 * 1. Loading initial config from the registered JSON (for fallback/testing)
 * 2. Creating Domain and BoundedContext instances from config
 *
 * NOTE: Workspace state is now synchronized from the server via WorkspaceCollaborationService.
 * localStorage persistence has been removed - the server is the source of truth.
 * The initial config is only used for seeding on the server side.
 */
import { DomainImpl, WorkspaceImpl, BoundedContextImpl } from '@semantical/core/modules/workspace';
import { createVocabularyBindingRef } from '@semantical/core/modules/vocabulary/VocabularyBindingImpl';
import Diagram from '@semantical/core/modules/diagram/Diagram';
/**
 * Default empty config used when no initial config is provided.
 */
const DEFAULT_EMPTY_CONFIG = {
    version: 1,
    domains: []
};
/**
 * Registered initial config (set via setInitialConfig before calling load functions).
 * This allows the web package to inject the actual config at runtime.
 */
let registeredInitialConfig;
/**
 * Extract a short ID from a URI/IRI.
 * Examples:
 * - "urn:semantical:domain:party-domain" → "party-domain"
 * - "http://example.org/party#" → "party#"
 */
function extractId(uri) {
    // For URN-style URIs, extract the last segment after ':'
    if (uri.startsWith('urn:')) {
        const parts = uri.split(':');
        return parts[parts.length - 1];
    }
    // For HTTP URIs, extract the fragment or last path segment
    if (uri.includes('#')) {
        return uri.split('#').pop() || uri;
    }
    // Extract last path segment
    const parts = uri.split('/');
    return parts[parts.length - 1] || uri;
}
/**
 * Storage key for workspace configuration.
 * @deprecated localStorage persistence has been removed. Server is now the source of truth.
 */
export const WORKSPACE_CONFIG_STORAGE_KEY = 'semantical.workspaceConfig';
/**
 * Register the initial workspace configuration.
 * This must be called by the web package before using loadWorkspaceConfig().
 *
 * @param config - The initial config (typically loaded from workspace-config.json)
 */
export function setInitialConfig(config) {
    registeredInitialConfig = config;
}
/**
 * Get the registered initial config or the default empty config.
 */
function getInitialConfig() {
    return registeredInitialConfig ?? DEFAULT_EMPTY_CONFIG;
}
/**
 * Load workspace configuration from registered initial config.
 *
 * NOTE: This now only loads from the registered initial config.
 * Server-side state should be loaded via WorkspaceCollaborationService.
 *
 * This creates Domain instances with their ontology references.
 * BoundedContexts are referenced but not fully instantiated here
 * (Workspace handles BoundedContext instantiation).
 */
export function loadWorkspaceConfig() {
    // Use initial codebase config (server sync will update later)
    console.debug('WorkspaceConfigLoader: Loading from initial config (server will sync)');
    const config = getInitialConfig();
    const domains = buildDomainHierarchy(config.domains);
    return { domains, config, fromStorage: false };
}
/**
 * Create a Workspace instance from the configuration.
 *
 * This is the primary entry point for initializing the application.
 * It loads the config and creates a WorkspaceImpl with the domain hierarchy.
 *
 * @returns Object containing the workspace and raw config
 */
export function createWorkspace() {
    const result = loadWorkspaceConfig();
    const workspace = new WorkspaceImpl({
        id: 'main-workspace',
        name: 'Semantical Workspace',
        version: result.config.version,
        rootDomains: result.domains
    });
    console.debug('WorkspaceConfigLoader: Created workspace with', {
        domainCount: result.domains.length,
        fromStorage: result.fromStorage
    });
    return {
        workspace,
        config: result.config,
        fromStorage: result.fromStorage
    };
}
/**
 * Load the initial workspace configuration from the codebase JSON only.
 * Use this when you explicitly want the default config, ignoring localStorage.
 */
export function loadInitialConfig() {
    const config = getInitialConfig();
    const domains = buildDomainHierarchy(config.domains);
    return { domains, config, fromStorage: false };
}
/**
 * Load workspace configuration from localStorage.
 * @deprecated localStorage persistence has been removed. Server is now the source of truth.
 * @returns Always returns null - use WorkspaceCollaborationService to sync from server.
 */
export function loadPersistedConfig() {
    console.debug('WorkspaceConfigLoader: loadPersistedConfig() is deprecated - server is source of truth');
    return null;
}
/**
 * Save workspace configuration to localStorage.
 * @deprecated localStorage persistence has been removed. Use WorkspaceApiClient to save to server.
 */
export function saveWorkspaceConfig(_config) {
    console.debug('WorkspaceConfigLoader: saveWorkspaceConfig() is deprecated - use WorkspaceApiClient');
    // No-op: Server is now the source of truth
}
/**
 * Clear workspace configuration from localStorage.
 * @deprecated localStorage persistence has been removed.
 */
export function clearPersistedConfig() {
    console.debug('WorkspaceConfigLoader: clearPersistedConfig() is deprecated');
    // Clean up any legacy data if it exists
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(WORKSPACE_CONFIG_STORAGE_KEY);
        }
    }
    catch {
        // Ignore errors (e.g., in Node.js environment)
    }
}
/**
 * Add or update a view reference in a BoundedContext's config.
 * @deprecated Use WorkspaceApiClient.addView() instead for server-side persistence.
 * Note: View content (classes, relationships, settings) is stored in DiagramRepository,
 * not in WorkspaceConfig - this only tracks which views exist in which BCs.
 */
export function addViewToConfig(config, boundedContextId, viewId, viewName, viewType = 'Diagram') {
    const contextConfig = findBoundedContextConfigById(config, boundedContextId);
    if (!contextConfig) {
        console.error(`Cannot add view: BoundedContext not found: ${boundedContextId}`);
        return;
    }
    // Initialize views array if needed
    if (!contextConfig.views) {
        contextConfig.views = [];
    }
    // Check if view already exists
    const existingIndex = contextConfig.views.findIndex(v => v.id === viewId);
    const viewRef = {
        id: viewId,
        name: viewName,
        viewType: viewType
    };
    if (existingIndex >= 0) {
        // Update existing - update name and type
        contextConfig.views[existingIndex].name = viewName;
        contextConfig.views[existingIndex].viewType = viewType;
    }
    else {
        // Add new
        contextConfig.views.push(viewRef);
    }
    // Note: No longer saving to localStorage - server is source of truth
    console.debug(`WorkspaceConfigLoader: Updated local config for view ${viewId} in ${boundedContextId} (use WorkspaceApiClient for persistence)`);
}
/**
 * Add or update a dataset reference in a BoundedContext's config.
 * @deprecated Use WorkspaceApiClient.addDataset() instead for server-side persistence.
 */
export function addDatasetToConfig(config, boundedContextId, datasetId, datasetName, datasetType = 'KnowledgeGraph') {
    const contextConfig = findBoundedContextConfigById(config, boundedContextId);
    if (!contextConfig) {
        console.error(`Cannot add dataset: BoundedContext not found: ${boundedContextId}`);
        return;
    }
    // Initialize datasets array if needed
    if (!contextConfig.datasets) {
        contextConfig.datasets = [];
    }
    // Check if dataset already exists
    const existingIndex = contextConfig.datasets.findIndex(d => d.id === datasetId);
    if (existingIndex >= 0) {
        // Update existing - just update the name
        contextConfig.datasets[existingIndex].name = datasetName;
    }
    else {
        // Add new dataset reference based on type
        if (datasetType === 'KnowledgeGraph') {
            contextConfig.datasets.push({
                type: 'KnowledgeGraph',
                id: datasetId,
                name: datasetName,
                namespace: `urn:semantical:dataset:${datasetId}#`,
                individuals: [],
                relationships: []
            });
        }
        else if (datasetType === 'SPARQLEndpoint') {
            contextConfig.datasets.push({
                type: 'SPARQLEndpoint',
                id: datasetId,
                name: datasetName,
                endpointUrl: ''
            });
        }
        else {
            contextConfig.datasets.push({
                type: 'LinkedDataResource',
                id: datasetId,
                name: datasetName,
                resourceUrl: ''
            });
        }
    }
    // Note: No longer saving to localStorage - server is source of truth
    console.debug(`WorkspaceConfigLoader: Updated local config for dataset ${datasetId} in ${boundedContextId} (use WorkspaceApiClient for persistence)`);
}
/**
 * Remove a dataset reference from a BoundedContext's config.
 * @deprecated Use WorkspaceApiClient.removeDataset() instead for server-side persistence.
 */
export function removeDatasetFromConfig(config, boundedContextId, datasetId) {
    const contextConfig = findBoundedContextConfigById(config, boundedContextId);
    if (!contextConfig || !contextConfig.datasets) {
        return;
    }
    const index = contextConfig.datasets.findIndex(d => d.id === datasetId);
    if (index >= 0) {
        contextConfig.datasets.splice(index, 1);
        // Note: No longer saving to localStorage - server is source of truth
        console.debug(`WorkspaceConfigLoader: Removed dataset ${datasetId} from local config (use WorkspaceApiClient for persistence)`);
    }
}
/**
 * Remove a view reference from a BoundedContext's config.
 * @deprecated Use WorkspaceApiClient.removeView() instead for server-side persistence.
 */
export function removeViewFromConfig(config, boundedContextId, viewId) {
    const contextConfig = findBoundedContextConfigById(config, boundedContextId);
    if (!contextConfig || !contextConfig.views) {
        return;
    }
    const index = contextConfig.views.findIndex(v => v.id === viewId);
    if (index >= 0) {
        contextConfig.views.splice(index, 1);
        // Note: No longer saving to localStorage - server is source of truth
        console.debug(`WorkspaceConfigLoader: Removed view ${viewId} from local config (use WorkspaceApiClient for persistence)`);
    }
}
/**
 * Find which BoundedContext owns a view by view ID.
 * Searches through all BCs in the config to find the one containing the view.
 * Returns the BC ID if found, undefined otherwise.
 */
export function findContextIdForView(config, viewId) {
    function searchInDomain(domainConfig) {
        // Check bounded contexts in this domain
        for (const context of domainConfig.contexts) {
            if (context.views?.some(v => v.id === viewId)) {
                return extractId(context['@id']);
            }
        }
        // Search children recursively
        for (const child of domainConfig.subdomains) {
            const found = searchInDomain(child);
            if (found)
                return found;
        }
        return undefined;
    }
    for (const domain of config.domains) {
        const found = searchInDomain(domain);
        if (found)
            return found;
    }
    return undefined;
}
/**
 * Find which BoundedContext contains a vocabulary by vocabulary URI.
 * Searches through all BCs in the config to find one containing the vocabulary.
 * Returns the first matching BC ID if found, undefined otherwise.
 * Note: The same vocabulary URI could exist in multiple BCs.
 */
export function findContextIdForVocabulary(config, vocabularyUri) {
    function searchInDomain(domainConfig) {
        // Check bounded contexts in this domain
        for (const context of domainConfig.contexts) {
            const vocabularies = context.hasUbiquitousLanguage?.includesVocabulary ?? [];
            if (vocabularies.some(v => v['@id'] === vocabularyUri)) {
                return extractId(context['@id']);
            }
        }
        // Search children recursively
        for (const child of domainConfig.subdomains) {
            const found = searchInDomain(child);
            if (found)
                return found;
        }
        return undefined;
    }
    for (const domain of config.domains) {
        const found = searchInDomain(domain);
        if (found)
            return found;
    }
    return undefined;
}
/**
 * Build Domain hierarchy from config array.
 * Recursively processes children, registers ontologies, and creates
 * BoundedContextImpl instances for all bounded contexts.
 *
 * BoundedContextImpl construction is lightweight - just identity and model shell.
 * Ontology content is loaded server-side via SPARQL.
 */
function buildDomainHierarchy(domainConfigs, parent) {
    return domainConfigs.map(config => {
        const domainUri = config['@id'];
        const domainId = extractId(domainUri);
        const domain = new DomainImpl({
            id: domainId,
            uri: domainUri,
            name: config.name,
            definition: config.definition,
            parent
        });
        // Register ontologies
        for (const ontology of config.ontologies) {
            const ontologyIri = ontology['@id'];
            domain.addOntology({
                id: ontologyIri, // Use IRI as ID
                label: ontology.label,
                namespace: ontologyIri
            });
        }
        // Register with parent
        if (parent) {
            parent.addChild(domain);
        }
        // Create BoundedContextImpl instances for all bounded contexts
        // This is lightweight - just identity and model shell
        for (const contextConfig of config.contexts) {
            const contextUri = contextConfig['@id'];
            const contextId = extractId(contextUri);
            const boundedContext = new BoundedContextImpl({
                id: contextId,
                name: contextConfig.name,
                definition: contextConfig.definition,
                domain: domain
            });
            // BoundedContextImpl constructor calls domain.addBoundedContext(this)
            // Initialize Ubiquitous Language from config
            if (contextConfig.hasUbiquitousLanguage) {
                const allVocabularies = contextConfig.hasUbiquitousLanguage.includesVocabulary ?? [];
                for (const vocabConfig of allVocabularies) {
                    boundedContext.addVocabularyBinding(createVocabularyBindingRef({
                        id: vocabConfig['@id'],
                        name: vocabConfig.label,
                        boundedContextId: contextId,
                        namespace: vocabConfig['@id'],
                        vocabularyIri: vocabConfig['@id'],
                    }));
                }
            }
            // Create shell Diagrams from config (content loaded on-demand from DiagramRepository)
            // ViewConfig only contains id, name, viewType - all settings come from DiagramRepository
            if (contextConfig.views) {
                for (const viewConfig of contextConfig.views) {
                    if (viewConfig.viewType === 'Diagram') {
                        const diagram = new Diagram(viewConfig.name, contextId, viewConfig.id);
                        // All diagram settings/content loaded from DiagramRepository when activated
                        boundedContext.registerView(diagram);
                    }
                    // Future: handle Matrix, Table view types
                }
            }
            // Load knowledge graphs from config
            // Note: The actual KG data is loaded lazily by PersistenceService when needed
            if (contextConfig.datasets && contextConfig.datasets.length > 0) {
                console.debug(`WorkspaceConfigLoader: Loading ${contextConfig.datasets.length} datasets for BC ${boundedContext.id}`);
                for (const datasetConfig of contextConfig.datasets) {
                    if (datasetConfig.type === 'KnowledgeGraph') {
                        // Create a KnowledgeGraph reference (interface, not full class)
                        const kgRef = {
                            id: datasetConfig.id,
                            name: datasetConfig.name,
                            uri: `urn:kg:${datasetConfig.id}`,
                            boundedContextId: boundedContext.id
                        };
                        // Register the reference - actual KG data loaded by persistence service
                        boundedContext.addKnowledgeGraph(kgRef);
                        console.debug(`WorkspaceConfigLoader: Loaded KnowledgeGraph ${kgRef.name} (${kgRef.id}) into BC ${boundedContext.id}`);
                    }
                }
                console.debug(`WorkspaceConfigLoader: BC ${boundedContext.id} now has ${boundedContext.getKnowledgeGraphs().length} knowledge graphs`);
            }
        }
        // Process children recursively
        if (config.subdomains && config.subdomains.length > 0) {
            buildDomainHierarchy(config.subdomains, domain);
        }
        return domain;
    });
}
/**
 * Find a domain by ID in the hierarchy.
 */
export function findDomainById(domains, id) {
    for (const domain of domains) {
        if (domain.id === id) {
            return domain;
        }
        const found = findDomainById(domain.getChildren(), id);
        if (found) {
            return found;
        }
    }
    return undefined;
}
/**
 * Find the domain ID that contains a bounded context with the given ID.
 * Searches the config structure (not runtime Domain hierarchy).
 */
export function findDomainIdForContextId(config, contextId) {
    function searchInDomain(domainConfig) {
        // Check if this domain has the context
        for (const context of domainConfig.contexts) {
            if (extractId(context['@id']) === contextId) {
                return extractId(domainConfig['@id']);
            }
        }
        // Search children recursively
        for (const child of domainConfig.subdomains) {
            const found = searchInDomain(child);
            if (found) {
                return found;
            }
        }
        return undefined;
    }
    for (const domain of config.domains) {
        const found = searchInDomain(domain);
        if (found) {
            return found;
        }
    }
    return undefined;
}
/**
 * Find the runtime Domain that should contain a bounded context with the given ID.
 * First finds the domain ID from config, then looks up the runtime Domain.
 */
export function findDomainForContextId(domains, config, contextId) {
    const domainId = findDomainIdForContextId(config, contextId);
    if (!domainId) {
        return undefined;
    }
    return findDomainById(domains, domainId);
}
/**
 * Find a bounded context config by ID in the config.
 */
export function findBoundedContextConfigById(config, contextId) {
    for (const domain of config.domains) {
        const found = findContextInDomain(domain, contextId);
        if (found) {
            return found;
        }
    }
    return undefined;
}
function findContextInDomain(domain, contextId) {
    for (const context of domain.contexts) {
        if (extractId(context['@id']) === contextId) {
            return context;
        }
    }
    for (const child of domain.subdomains) {
        const found = findContextInDomain(child, contextId);
        if (found) {
            return found;
        }
    }
    return undefined;
}
/**
 * Get all bounded context configs from the configuration.
 */
export function getAllBoundedContextConfigs(config) {
    const contexts = [];
    function collectFromDomain(domain) {
        contexts.push(...domain.contexts);
        for (const child of domain.subdomains) {
            collectFromDomain(child);
        }
    }
    for (const domain of config.domains) {
        collectFromDomain(domain);
    }
    return contexts;
}
/**
 * Find a runtime BoundedContext (Workspace) by ID in the domain hierarchy.
 * Returns the actual Workspace instance registered with the domain.
 */
export function findBoundedContextById(domains, contextId) {
    for (const domain of domains) {
        // Check bounded contexts in this domain
        for (const context of domain.getBoundedContexts()) {
            if (context.id === contextId) {
                return context;
            }
        }
        // Search children recursively
        const found = findBoundedContextById(domain.getChildren(), contextId);
        if (found) {
            return found;
        }
    }
    return undefined;
}
/**
 * Get the first imported ontology IRI from a bounded context config.
 * @deprecated Ontology IRI should be derived from OntologyBinding, not config.
 */
export function getPrimaryOntologyIri(_context) {
    return undefined;
}
/**
 * Find a view config by ID in the workspace config.
 * Returns the view config if found, undefined otherwise.
 */
export function findViewConfigById(config, viewId) {
    function searchInDomain(domainConfig) {
        for (const context of domainConfig.contexts) {
            const view = context.views?.find(v => v.id === viewId);
            if (view) {
                return view;
            }
        }
        for (const child of domainConfig.subdomains) {
            const found = searchInDomain(child);
            if (found)
                return found;
        }
        return undefined;
    }
    for (const domain of config.domains) {
        const found = searchInDomain(domain);
        if (found)
            return found;
    }
    return undefined;
}
